import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Legacy /admin/* deep-link bridge. Any incoming /admin/<sub-path>?<query>
// is rewritten to /adminpanel/<sub-path>?<query> while preserving query string,
// so older notification emails / bookmarks keep working forever.
function AdminLegacyRedirect() {
  const { "*": rest } = useParams();
  const { search } = useLocation();
  const target = `/adminpanel/${rest || ""}${search || ""}`;
  return <Navigate to={target} replace />;
}

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import SupportPlan from "@/pages/SupportPlan";
import Membership from "@/pages/Membership";
import MembershipSuccess from "@/pages/MembershipSuccess";
import QuoteReview from "@/pages/QuoteReview";
import Insurance from "@/pages/Insurance";
import RequestSupport from "@/pages/RequestSupport";
import PayInvoice from "@/pages/PayInvoice";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Operators from "@/pages/Operators";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Inspections from "@/pages/Inspections";
import UrgentRepairs from "@/pages/UrgentRepairs";
import ReportViewer from "@/pages/ReportViewer";
import {
  ParkHomeRepairCostsUK,
  ParkHomeRoofLeakAdvice,
  ParkHomeDampProblems,
  ParkHomeInsuranceClaimsAdvice,
  ParkHomeQuoteTooHigh,
} from "@/pages/seo/SeoLandingPages";

import {
  ParkHomeAssistAlternative, GoldshieldAlternative, ParkHomeInsuranceAlternatives, BestParkHomeSupportServices,
  MaintenancePlanAlternative,
  ParkHomeInspection, ParkHomeRepairs, ParkHomeMaintenance, ParkHomeInsuranceClaimSupport, ParkHomeQuoteReview,
  LocationEssex, LocationKent, LocationSuffolk, LocationNorfolk, LocationCambridgeshire,
  InspectionsEssex, RepairsEssex, SurveysKent, RepairsSuffolk, InsuranceHelpNorfolk, MaintenanceCambridgeshire,
  RepairsColchester, SurveysChelmsford, SupportClacton,
  InspectionCost, UnderfloorInsulation,
  KbResponsibility, KbOperatorRefuse, KbClaimRejected, KbBaseFailure, KbMobileHomesAct, KbDrainage,
} from "@/pages/seo/SeoExtraPages";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminQuoteReviews from "@/pages/admin/QuoteReviews";
import AdminSupportCases from "@/pages/admin/SupportCases";
import AdminMembers from "@/pages/admin/Members";
import AdminPayments from "@/pages/admin/Payments";
import AdminInvoices from "@/pages/admin/Invoices";
import AdminAutomation from "@/pages/admin/Automation";
import AdminContactEnquiries from "@/pages/admin/ContactEnquiries";
import AdminOperatorEnquiries from "@/pages/admin/OperatorEnquiries";
import AdminSettings from "@/pages/admin/Settings";
import AdminSeoLeads from "@/pages/admin/SeoLeads";
import AdminReviews from "@/pages/admin/Reviews";
import AdminContractorPayments from "@/pages/admin/ContractorPayments";
import AdminIntelOperators from "@/pages/admin/IntelligenceOperators";
import AdminIntelParks from "@/pages/admin/IntelligenceParks";
import AdminIntelRepairCosts from "@/pages/admin/IntelligenceRepairCosts";
import AdminWorkspace from "@/pages/admin/workspace/Workspace";
import AdminCommsHub from "@/pages/admin/CommsHub";
import AdminContractors from "@/pages/admin/Contractors";
import AdminMargin from "@/pages/admin/MarginDashboard";
import AdminOperatorOverview from "@/pages/admin/OperatorOverview";
import AdminHealthChecks from "@/pages/admin/HealthChecks";
import AdminHealthCheckEditor from "@/pages/admin/HealthCheckEditor";
import AdminParks from "@/pages/admin/assets/Parks";
import AdminParkRecord from "@/pages/admin/assets/ParkRecord";
import AdminPlots from "@/pages/admin/assets/Plots";
import AdminPlotRecord from "@/pages/admin/assets/PlotRecord";
import LeaveReview from "@/pages/LeaveReview";
import InvoicePay from "@/pages/InvoicePay";
import MyHealthChecks from "@/pages/MyHealthChecks";
import HealthCheckViewer from "@/pages/HealthCheckViewer";
import Analytics from "@/components/Analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ReferralTracker() {
  const { search } = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(search);
    const ref = params.get("ref");
    if (ref) {
      try {
        // 90-day cookie; also write to localStorage as fallback for safari ITP
        const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `phs_ref=${encodeURIComponent(ref)}; expires=${expires}; path=/; SameSite=Lax`;
        localStorage.setItem("phs_ref", ref);
      } catch {
        /* ignore */
      }
    }
  }, [search]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <ReferralTracker />
          <Analytics />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/support-plan" element={<SupportPlan />} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/membership/success" element={<MembershipSuccess />} />
              <Route path="/quote-review" element={<QuoteReview />} />
              <Route path="/insurance" element={<Insurance />} />
              <Route path="/inspections" element={<Inspections />} />
              <Route path="/park-home-urgent-repairs" element={<UrgentRepairs />} />
              <Route path="/request-support" element={<RequestSupport />} />
              <Route path="/pay" element={<PayInvoice />} />
              <Route path="/pay/:invoiceNumber" element={<InvoicePay />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/operators" element={<Operators />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/leave-review" element={<LeaveReview />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/report/:token" element={<ReportViewer />} />
              <Route path="/my-health-checks" element={<MyHealthChecks />} />
              <Route path="/health-checks/:token" element={<HealthCheckViewer />} />
              {/* SEO landing pages */}
              <Route path="/park-home-repair-costs-uk"         element={<ParkHomeRepairCostsUK />} />
              <Route path="/park-home-roof-leak-advice"        element={<ParkHomeRoofLeakAdvice />} />
              <Route path="/park-home-damp-problems"           element={<ParkHomeDampProblems />} />
              <Route path="/park-home-insurance-claims-advice" element={<ParkHomeInsuranceClaimsAdvice />} />
              <Route path="/park-home-quote-too-high"           element={<ParkHomeQuoteTooHigh />} />

              {/* Competitor comparison */}
              <Route path="/park-home-assist-alternative"        element={<ParkHomeAssistAlternative />} />
              <Route path="/goldshield-alternative"              element={<GoldshieldAlternative />} />
              <Route path="/park-home-insurance-alternatives"    element={<ParkHomeInsuranceAlternatives />} />
              <Route path="/best-park-home-support-services"     element={<BestParkHomeSupportServices />} />
              <Route path="/park-home-maintenance-plan-alternative" element={<MaintenancePlanAlternative />} />

              {/* Service pages */}
              <Route path="/park-home-inspection"                element={<ParkHomeInspection />} />
              <Route path="/park-home-repairs"                   element={<ParkHomeRepairs />} />
              <Route path="/park-home-maintenance"               element={<ParkHomeMaintenance />} />
              <Route path="/park-home-insurance-claim-support"   element={<ParkHomeInsuranceClaimSupport />} />
              <Route path="/park-home-quote-review-service"      element={<ParkHomeQuoteReview />} />

              {/* Location pages */}
              <Route path="/park-home-support-essex"             element={<LocationEssex />} />
              <Route path="/park-home-support-kent"              element={<LocationKent />} />
              <Route path="/park-home-support-suffolk"           element={<LocationSuffolk />} />
              <Route path="/park-home-support-norfolk"           element={<LocationNorfolk />} />
              <Route path="/park-home-support-cambridgeshire"    element={<LocationCambridgeshire />} />

              {/* Service+location pages */}
              <Route path="/park-home-inspections-essex"         element={<InspectionsEssex />} />
              <Route path="/park-home-repairs-essex"             element={<RepairsEssex />} />
              <Route path="/park-home-surveys-kent"              element={<SurveysKent />} />
              <Route path="/park-home-repairs-suffolk"           element={<RepairsSuffolk />} />
              <Route path="/park-home-insurance-help-norfolk"    element={<InsuranceHelpNorfolk />} />
              <Route path="/park-home-maintenance-cambridgeshire" element={<MaintenanceCambridgeshire />} />

              {/* City pages */}
              <Route path="/park-home-repairs-colchester"        element={<RepairsColchester />} />
              <Route path="/park-home-surveys-chelmsford"        element={<SurveysChelmsford />} />
              <Route path="/park-home-support-clacton"           element={<SupportClacton />} />

              {/* Intent-specific problem pages */}
              <Route path="/park-home-inspection-cost"           element={<InspectionCost />} />
              <Route path="/park-home-underfloor-insulation-problem" element={<UnderfloorInsulation />} />

              {/* Knowledge centre */}
              <Route path="/who-is-responsible-for-park-home-repairs" element={<KbResponsibility />} />
              <Route path="/can-park-operator-refuse-repairs"        element={<KbOperatorRefuse />} />
              <Route path="/park-home-insurance-claim-rejected"      element={<KbClaimRejected />} />
              <Route path="/park-home-base-failure-signs"            element={<KbBaseFailure />} />
              <Route path="/understanding-mobile-homes-act"          element={<KbMobileHomesAct />} />
              <Route path="/park-home-drainage-problems"             element={<KbDrainage />} />

              {/* Canonical-path redirects (preserve any older marketing links) */}
              <Route path="/operator" element={<Navigate to="/operators" replace />} />
              <Route path="/pay-invoice" element={<Navigate to="/pay" replace />} />
            </Route>

            {/* Admin routes — canonical /adminpanel path, key-gated */}
            <Route path="/adminpanel/login" element={<AdminLogin />} />
            <Route path="/adminpanel" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="quote-reviews" element={<AdminQuoteReviews />} />
              <Route path="support-cases" element={<AdminSupportCases />} />
              <Route path="seo-leads" element={<AdminSeoLeads />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="contractor-payments" element={<AdminContractorPayments />} />
              <Route path="intel/operators" element={<AdminIntelOperators />} />
              <Route path="intel/parks" element={<AdminIntelParks />} />
              <Route path="intel/repair-costs" element={<AdminIntelRepairCosts />} />
              <Route path="workspace/:caseId" element={<AdminWorkspace />} />
              <Route path="comms" element={<AdminCommsHub />} />
              <Route path="contractors" element={<AdminContractors />} />
              <Route path="margin" element={<AdminMargin />} />
              <Route path="operator-overview" element={<AdminOperatorOverview />} />
              <Route path="health-checks" element={<AdminHealthChecks />} />
              <Route path="health-checks/:id" element={<AdminHealthCheckEditor />} />
              <Route path="assets/parks" element={<AdminParks />} />
              <Route path="assets/parks/:parkId" element={<AdminParkRecord />} />
              <Route path="assets/plots" element={<AdminPlots />} />
              <Route path="assets/plots/:plotId" element={<AdminPlotRecord />} />
              <Route path="members" element={<AdminMembers />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="automation" element={<AdminAutomation />} />
              <Route path="contact" element={<AdminContactEnquiries />} />
              <Route path="operators" element={<AdminOperatorEnquiries />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            {/* Backwards-compat: keep older /admin/* deep links working forever.
                Preserves the rest of the path AND any query string (e.g. ?case_id=...) */}
            <Route path="/admin" element={<Navigate to="/adminpanel" replace />} />
            <Route path="/admin/*" element={<AdminLegacyRedirect />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
